import { useState } from 'react'
import type {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  RowSelectionState,
} from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  useReactTable,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  MoreHorizontal,
  Pencil,
  Copy,
  Trash2,
  Settings2,
  Building2,
  Warehouse,
  CheckCircle,
  AlertCircle,
  Construction,
  XCircle,
} from 'lucide-react'
import {
  DataTableColumnHeader,
  DataTableFacetedFilter,
  DataTablePagination,
  DataTableToolbar,
} from '@/components/data-table'
import type { HangarTableRow, HangarType, HangarStatus } from '@/types/hangars'
import {
  HANGAR_TYPE_LABELS,
  HANGAR_STATUS_LABELS,
  HANGAR_TYPES,
  HANGAR_STATUSES,
  isTHangarType,
} from '@/types/hangars'

interface HangarsTableProps {
  /** Hangar data to display */
  data: HangarTableRow[]
  /** Loading state */
  isLoading: boolean
  /** Callback when edit action is triggered */
  onEdit?: (id: string) => void
  /** Callback when duplicate action is triggered */
  onDuplicate?: (id: string) => void
  /** Callback when delete action is triggered */
  onDelete: (id: string, name: string) => void
  /** Callback when configure units action is triggered */
  onConfigureUnits: (id: string) => void
}

/** Badge variant mapping for hangar status */
const statusVariants: Record<HangarStatus, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  active: 'default',
  maintenance: 'secondary',
  construction: 'outline',
  inactive: 'destructive',
}

/** Badge variant mapping for hangar type */
const typeVariants: Record<HangarType, 'default' | 'secondary' | 'outline'> = {
  single_unit_hangar: 'default',
  box_hangar_single_row: 'default',
  box_hangar_double_row: 'default',
  t_hangar_row: 'outline',
  nested_t_hangar_row: 'outline',
  tiedown_area: 'outline',
  ramp_area: 'outline',
  shade_structure: 'secondary',
  terminal: 'secondary',
  office: 'secondary',
  custom: 'secondary',
}

/** Type filter options with icons */
const typeFilterOptions = HANGAR_TYPES.map((type) => ({
  label: HANGAR_TYPE_LABELS[type],
  value: type,
  icon: ['t_hangar_row', 'nested_t_hangar_row'].includes(type) ? Warehouse : Building2,
}))

/** Status filter options with icons */
const statusFilterOptions = HANGAR_STATUSES.map((status) => ({
  label: HANGAR_STATUS_LABELS[status],
  value: status,
  icon:
    status === 'active'
      ? CheckCircle
      : status === 'maintenance'
        ? AlertCircle
        : status === 'construction'
          ? Construction
          : XCircle,
}))

export function HangarsTable({
  data,
  isLoading,
  onEdit,
  onDuplicate,
  onDelete,
  onConfigureUnits,
}: HangarsTableProps) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [globalFilter, setGlobalFilter] = useState('')

  const columns: ColumnDef<HangarTableRow>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Name" />
      ),
      cell: ({ row }) => (
        <div className="font-medium">{row.getValue('name')}</div>
      ),
      enableHiding: false,
    },
    {
      accessorKey: 'area_code',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Code" />
      ),
      cell: ({ row }) => (
        <span className="font-mono text-sm">{row.getValue('area_code')}</span>
      ),
    },
    {
      accessorKey: 'type',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Type" />
      ),
      cell: ({ row }) => {
        const type = row.getValue('type') as HangarType
        return (
          <Badge variant={typeVariants[type] || 'default'}>
            {HANGAR_TYPE_LABELS[type] || type}
          </Badge>
        )
      },
      filterFn: (row, id, value: string[]) => {
        if (!value || value.length === 0) return true
        return value.includes(row.getValue(id))
      },
    },
    {
      id: 'dimensions',
      header: 'Dimensions (ft)',
      cell: ({ row }) => {
        const { exterior_width, exterior_depth, exterior_height } = row.original
        return (
          <span className="text-sm text-muted-foreground">
            {exterior_width} × {exterior_depth} × {exterior_height}
          </span>
        )
      },
    },
    {
      accessorKey: 'total_footprint_sqft',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Sq. Ft." />
      ),
      cell: ({ row }) => {
        const sqft = row.getValue('total_footprint_sqft') as number
        return (
          <span className="font-mono text-sm">
            {sqft.toLocaleString()}
          </span>
        )
      },
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => {
        const status = row.getValue('status') as HangarStatus
        return (
          <Badge variant={statusVariants[status] || 'default'}>
            {HANGAR_STATUS_LABELS[status] || status}
          </Badge>
        )
      },
      filterFn: (row, id, value: string[]) => {
        if (!value || value.length === 0) return true
        return value.includes(row.getValue(id))
      },
    },
    {
      id: 'units',
      header: 'Units',
      cell: ({ row }) => {
        const hangar = row.original
        const isT = isTHangarType(hangar.type)

        if (!isT) {
          return <span className="text-muted-foreground">—</span>
        }

        const unitCount = hangar.unit_count ?? 0

        return (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onConfigureUnits(hangar.id)}
            className="h-7 px-2 text-xs"
          >
            <Settings2 className="mr-1 h-3 w-3" />
            {unitCount > 0 ? `${unitCount} units` : 'Configure'}
          </Button>
        )
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {onEdit && (
              <DropdownMenuItem onClick={() => onEdit(row.original.id)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
            )}
            {onDuplicate && (
              <DropdownMenuItem onClick={() => onDuplicate(row.original.id)}>
                <Copy className="mr-2 h-4 w-4" />
                Duplicate
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() => onDelete(row.original.id, row.original.name)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
      enableHiding: false,
    },
  ]

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    globalFilterFn: (row, _columnId, filterValue) => {
      const search = filterValue.toLowerCase()
      const name = (row.original.name || '').toLowerCase()
      const code = (row.original.area_code || '').toLowerCase()
      return name.includes(search) || code.includes(search)
    },
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter,
    },
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  })

  // Export to CSV
  const handleExport = () => {
    const headers = ['Name', 'Code', 'Type', 'Width', 'Depth', 'Height', 'Sq. Ft.', 'Status']
    const rows = table.getFilteredRowModel().rows.map((row) => [
      row.original.name,
      row.original.area_code,
      HANGAR_TYPE_LABELS[row.original.type] || row.original.type,
      row.original.exterior_width.toString(),
      row.original.exterior_depth.toString(),
      row.original.exterior_height.toString(),
      row.original.total_footprint_sqft.toLocaleString(),
      HANGAR_STATUS_LABELS[row.original.status] || row.original.status,
    ])

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(','))
      .join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `buildings_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
  }

  return (
    <div className="space-y-4">
      {/* Toolbar with search, filters, and view options */}
      <DataTableToolbar
        table={table}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        filterPlaceholder="Search buildings..."
        onExport={handleExport}
        exportLabel="Export CSV"
      >
        <DataTableFacetedFilter
          column={table.getColumn('type')}
          title="Type"
          options={typeFilterOptions}
        />
        <DataTableFacetedFilter
          column={table.getColumn('status')}
          title="Status"
          options={statusFilterOptions}
        />
      </DataTableToolbar>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <div className="text-muted-foreground">
                    Loading buildings...
                  </div>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <div className="text-muted-foreground">
                    No buildings found. Add your first building to get started.
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Enhanced Pagination */}
      <DataTablePagination table={table} />
    </div>
  )
}
